import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Table,
  Input,
} from 'reactstrap';
import ConfirmModal from '../../components/ConfirmModal';
import { ventasService } from '../../services/ventasService';

function VentaDetalle({ venta, onClose, onCancel, usuarioId }) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEstatusBadge = (estatus) => {
    const estatusLower = (estatus || '').toLowerCase();
    if (estatusLower === 'completada') {
      return <Badge color="success">Completada</Badge>;
    } else if (estatusLower === 'cancelada') {
      return <Badge color="danger">Cancelada</Badge>;
    }
    return <Badge color="secondary">{estatus || 'Desconocido'}</Badge>;
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!motivoCancelacion.trim()) {
      setError('El motivo de cancelación es requerido');
      return;
    }

    try {
      setCancelando(true);
      const ventaId = venta.VentaID || venta.ventaID;

      const response = await ventasService.cancelar(ventaId, {
        UsuarioID: usuarioId,
        Motivo: motivoCancelacion.trim(),
      });

      if (response.success) {
        setSuccess('Venta cancelada exitosamente');
        setTimeout(() => {
          setSuccess('');
          setShowCancelModal(false);
          onClose();
        }, 2000);
      } else {
        setError(response.Message || response.message || 'Error al cancelar la venta');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setCancelando(false);
    }
  };

  const estatusLower = (venta.Estatus || venta.estatus || '').toLowerCase();
  const puedeCancelar = estatusLower !== 'cancelada';

  // Normalizar datos
  const ventaNormalizada = {
    VentaID: venta.VentaID || venta.ventaID,
    SucursalNombre: venta.SucursalNombre || venta.sucursalNombre || '',
    ClienteNombre: venta.ClienteNombre || venta.clienteNombre,
    UsuarioNombre: venta.UsuarioNombre || venta.usuarioNombre || '',
    Folio: venta.Folio || venta.folio,
    Subtotal: venta.Subtotal !== undefined ? parseFloat(venta.Subtotal) : (venta.subtotal !== undefined ? parseFloat(venta.subtotal) : 0),
    DescuentoTotal: venta.DescuentoTotal !== undefined ? parseFloat(venta.DescuentoTotal) : (venta.descuentoTotal !== undefined ? parseFloat(venta.descuentoTotal) : 0),
    Impuestos: venta.Impuestos !== undefined ? parseFloat(venta.Impuestos) : (venta.impuestos !== undefined ? parseFloat(venta.impuestos) : 0),
    Total: venta.Total !== undefined ? parseFloat(venta.Total) : (venta.total !== undefined ? parseFloat(venta.total) : 0),
    Estatus: venta.Estatus || venta.estatus || '',
    FechaVenta: venta.FechaVenta || venta.fechaVenta,
    Observaciones: venta.Observaciones || venta.observaciones,
    Detalles: (venta.Detalles || venta.detalles || []).map(det => ({
      VentaDetalleID: det.VentaDetalleID || det.ventaDetalleID,
      ProductoID: det.ProductoID || det.productoID,
      ProductoNombre: det.ProductoNombre || det.productoNombre || '',
      ProductoSKU: det.ProductoSKU || det.productoSKU || '',
      Cantidad: det.Cantidad !== undefined ? parseFloat(det.Cantidad) : (det.cantidad !== undefined ? parseFloat(det.cantidad) : 0),
      PrecioUnitario: det.PrecioUnitario !== undefined ? parseFloat(det.PrecioUnitario) : (det.precioUnitario !== undefined ? parseFloat(det.precioUnitario) : 0),
      Descuento: det.Descuento !== undefined ? parseFloat(det.Descuento) : (det.descuento !== undefined ? parseFloat(det.descuento) : 0),
      Subtotal: det.Subtotal !== undefined ? parseFloat(det.Subtotal) : (det.subtotal !== undefined ? parseFloat(det.subtotal) : 0),
      Impuestos: det.Impuestos !== undefined ? parseFloat(det.Impuestos) : (det.impuestos !== undefined ? parseFloat(det.impuestos) : 0),
      Total: det.Total !== undefined ? parseFloat(det.Total) : (det.total !== undefined ? parseFloat(det.total) : 0),
    })),
    MetodosPago: (venta.MetodosPago || venta.metodosPago || []).map(mp => ({
      VentaMetodoPagoID: mp.VentaMetodoPagoID || mp.ventaMetodoPagoID,
      MetodoPagoID: mp.MetodoPagoID || mp.metodoPagoID,
      MetodoPagoNombre: mp.MetodoPagoNombre || mp.metodoPagoNombre || '',
      Monto: mp.Monto !== undefined ? parseFloat(mp.Monto) : (mp.monto !== undefined ? parseFloat(mp.monto) : 0),
    })),
  };

  const exportarVentaCSV = () => {
    const folio = ventaNormalizada.Folio || `VENT-${ventaNormalizada.VentaID}`;
    
    // Crear contenido CSV
    let csvContent = 'VENTA\n';
    csvContent += `Folio,${folio}\n`;
    csvContent += `Fecha,${formatDate(ventaNormalizada.FechaVenta)}\n`;
    csvContent += `Cliente,${ventaNormalizada.ClienteNombre || 'Cliente General'}\n`;
    csvContent += `Vendedor,${ventaNormalizada.UsuarioNombre}\n`;
    csvContent += `Sucursal,${ventaNormalizada.SucursalNombre}\n`;
    csvContent += `Estatus,${ventaNormalizada.Estatus}\n`;
    csvContent += `Subtotal,${formatCurrency(ventaNormalizada.Subtotal)}\n`;
    csvContent += `Descuento Total,${formatCurrency(ventaNormalizada.DescuentoTotal)}\n`;
    csvContent += `Impuestos,${formatCurrency(ventaNormalizada.Impuestos)}\n`;
    csvContent += `Total,${formatCurrency(ventaNormalizada.Total)}\n`;
    if (ventaNormalizada.Observaciones) {
      csvContent += `Observaciones,"${ventaNormalizada.Observaciones.replace(/"/g, '""')}"\n`;
    }
    
    // Métodos de pago
    if (ventaNormalizada.MetodosPago && ventaNormalizada.MetodosPago.length > 0) {
      csvContent += '\n';
      csvContent += 'MÉTODOS DE PAGO\n';
      csvContent += 'Método de Pago,Monto\n';
      ventaNormalizada.MetodosPago.forEach(mp => {
        const metodoNombre = (mp.MetodoPagoNombre || '').replace(/"/g, '""');
        csvContent += `"${metodoNombre}",${mp.Monto}\n`;
      });
    }
    
    csvContent += '\n';
    csvContent += 'DETALLES DE PRODUCTOS\n';
    csvContent += 'Producto,SKU,Cantidad,Precio Unitario,Descuento,Impuestos,Subtotal,Total\n';
    
    ventaNormalizada.Detalles.forEach(det => {
      const producto = (det.ProductoNombre || '').replace(/"/g, '""');
      const sku = (det.ProductoSKU || '').replace(/"/g, '""');
      csvContent += `"${producto}","${sku}",${det.Cantidad},${det.PrecioUnitario},${det.Descuento},${det.Impuestos},${det.Subtotal},${det.Total}\n`;
    });

    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `venta_${folio}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Detalle de Venta #{ventaNormalizada.VentaID}</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button 
                    color="info" 
                    onClick={exportarVentaCSV} 
                    className="mr-2"
                  >
                    <i className="tim-icons icon-cloud-download-93" /> Exportar CSV
                  </Button>
                  {puedeCancelar && (
                    <Button color="danger" onClick={handleCancel} className="mr-2">
                      <i className="tim-icons icon-simple-remove" /> Cancelar Venta
                    </Button>
                  )}
                  <Button color="secondary" onClick={onClose}>
                    <i className="tim-icons icon-simple-remove" /> Cerrar
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <Row className="mb-4">
                <Col md="6">
                  <h6>Información General</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Folio:</strong></td>
                        <td>{ventaNormalizada.Folio || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Fecha:</strong></td>
                        <td>{formatDate(ventaNormalizada.FechaVenta)}</td>
                      </tr>
                      <tr>
                        <td><strong>Sucursal:</strong></td>
                        <td>{ventaNormalizada.SucursalNombre}</td>
                      </tr>
                      <tr>
                        <td><strong>Cliente:</strong></td>
                        <td>{ventaNormalizada.ClienteNombre || 'Cliente General'}</td>
                      </tr>
                      <tr>
                        <td><strong>Vendedor:</strong></td>
                        <td>{ventaNormalizada.UsuarioNombre}</td>
                      </tr>
                      <tr>
                        <td><strong>Estatus:</strong></td>
                        <td>{getEstatusBadge(ventaNormalizada.Estatus)}</td>
                      </tr>
                      {ventaNormalizada.Observaciones && (
                        <tr>
                          <td><strong>Observaciones:</strong></td>
                          <td>{ventaNormalizada.Observaciones}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
                <Col md="6">
                  <h6>Totales</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Subtotal:</strong></td>
                        <td className="text-right">{formatCurrency(ventaNormalizada.Subtotal)}</td>
                      </tr>
                      <tr>
                        <td><strong>Descuento Total:</strong></td>
                        <td className="text-right">{formatCurrency(ventaNormalizada.DescuentoTotal)}</td>
                      </tr>
                      <tr>
                        <td><strong>Impuestos:</strong></td>
                        <td className="text-right">{formatCurrency(ventaNormalizada.Impuestos)}</td>
                      </tr>
                      <tr>
                        <td><strong>Total:</strong></td>
                        <td className="text-right">
                          <h5>{formatCurrency(ventaNormalizada.Total)}</h5>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md="12">
                  <h6>Detalles de Productos</h6>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>SKU</th>
                        <th>Cantidad</th>
                        <th>Precio Unit.</th>
                        <th>Descuento</th>
                        <th>Impuestos</th>
                        <th>Subtotal</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ventaNormalizada.Detalles.map((detalle, index) => (
                        <tr key={index}>
                          <td>{detalle.ProductoNombre}</td>
                          <td>{detalle.ProductoSKU}</td>
                          <td>{detalle.Cantidad.toFixed(2)}</td>
                          <td>{formatCurrency(detalle.PrecioUnitario)}</td>
                          <td>{formatCurrency(detalle.Descuento)}</td>
                          <td>{formatCurrency(detalle.Impuestos)}</td>
                          <td>{formatCurrency(detalle.Subtotal)}</td>
                          <td>{formatCurrency(detalle.Total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <h6>Métodos de Pago</h6>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Método de Pago</th>
                        <th>Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ventaNormalizada.MetodosPago.map((mp, index) => (
                        <tr key={index}>
                          <td>{mp.MetodoPagoNombre}</td>
                          <td>{formatCurrency(mp.Monto)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td><strong>Total:</strong></td>
                        <td>
                          <strong>
                            {formatCurrency(
                              ventaNormalizada.MetodosPago.reduce((sum, mp) => sum + mp.Monto, 0)
                            )}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showCancelModal}
        toggle={() => {
          setShowCancelModal(false);
          setMotivoCancelacion('');
          setError('');
        }}
        title="Cancelar Venta"
        message={
          <div>
            <p>¿Está seguro de cancelar esta venta?</p>
            <Input
              type="textarea"
              placeholder="Motivo de cancelación (requerido)"
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
              rows="3"
              className="mt-2"
            />
          </div>
        }
        confirmText="Cancelar Venta"
        cancelText="Cerrar"
        onConfirm={confirmCancel}
        loading={cancelando}
      />
    </div>
  );
}

export default VentaDetalle;

